import { applyDefaultButtonAnimation } from 'System/UI/UIHelper';
import Overlay from 'Prototype/UI/Overlay';
import Element from '../../../../Prototype/UI/Element';
import GameObject, { GameObjectEvent } from 'GameObject/GameObject';
import { StartAnimation } from 'System/Animation/AnimationHelper';
import { PlaySound } from 'System/Audio/AudioHelper';
import Renderable from 'Component/Renderable';
import { AnimationClipEvent } from 'System/Animation/AnimationClip';
import { SeasonPassPurchase } from 'Layouts/Quests/SeasonPass/SeasonPassPurchase';
import { isShopSupported } from 'Utility/Platform';
import Game from 'Game';
import { ProductId } from 'Utility/Product';
import TextComponent from 'Component/Render/Text';
import { ContextType } from 'State/Game/ContextType';
import { GameActions } from 'State/Game/GameActions';
import { IAPHelper } from 'Utility/Helpers/IAP/IAPHelper';
import { SeasonPassPurchaseErrorPopup } from './SeasonPassPurchaseErrorPopup';
import Armature from 'Prototype/Armature';
import { Point } from 'pixi.js';
import CleanupManager from 'Utility/CleanupManager';

export class SeasonPassPurchasePopup extends Element {
    private overlay: Overlay;
    private layout: any;
    private purchaseArmature: Armature;
    private errorPopup: SeasonPassPurchaseErrorPopup;
    private readonly cleanupManager: CleanupManager = new CleanupManager();

    public create(): void {
        super.create({
            type: 'group',
            layout: {
                anchors: [0.5, 0.5, 0.5, 0.5],
                pivot: [0.5, 0.5],
                offsets: [0, 0, 0, 0],
            },
        });

        this.overlay = GameObject.Instantiate(Overlay, {
            name: 'overlay',
            preventInteraction: true,
            anchors: [0, 0, 0, 0],
            alpha: 0.9,
            color: [0, 0, 0, 225],
        });
        this.addChild(this.overlay);

        this.layout = new SeasonPassPurchase();
        this.addChild(this.layout.root);

        this.purchaseArmature = GameObject.Instantiate(Armature, {
            dragonbonesName: 'premium_purchase',
            armatureName: 'Armature',
            alpha: 1,
            playOnStart: true,
            position: new Point(0, 0),
        });
        this.layout.background.addChild(this.purchaseArmature);
        this.cleanupManager.add(() => {
            this.purchaseArmature.destroy();
        });

        const { closeButton, purchaseText, purchaseButton, contentText } = this.layout;
        [closeButton].forEach(o => applyDefaultButtonAnimation(o));
        closeButton.once(GameObjectEvent.POINTER_UP, () => {
            this.destroy();
        });

        this.cleanupManager.add(() => {
            closeButton.off(GameObjectEvent.POINTER_UP);
        });

        if (isShopSupported()) {
            const price = Game.sdk.getCatalog().products.find((e) => e.productId === ProductId.PREMIUM_SEASON_PASS).price;
            purchaseText.getComponent(TextComponent).text = price;
            contentText.getComponent(TextComponent).text = Game.locale.getText('SeasonPassPurchaseDescription');
        } else {
            purchaseText.getComponent(TextComponent).text = Game.locale.getText('Ok');
            contentText.getComponent(TextComponent).text = Game.locale.getText('SeasonPassNoIAPDescription');
        }

        purchaseButton.on(GameObjectEvent.POINTER_UP, () => {
            if (isShopSupported()) {
                const product = IAPHelper.CACHED_IAP_REWARDS_MAP()[ProductId.PREMIUM_SEASON_PASS];
                GameActions.setContext(ContextType.PURCHASE_PRODUCT, {
                    product,
                });
            } else {
                this.destroy();
            }
        });

        this.cleanupManager.add(() => {
            purchaseButton.off(GameObjectEvent.POINTER_UP);
        });

        this.show();
    }

    public show() {
        this.getComponent(Renderable).visible = false;
        this.overlay.getComponent(Renderable).alpha = 0;
        PlaySound('swoosh-1');

        StartAnimation(this, 'MenuShow');
        StartAnimation(this.overlay, 'OverlayFadeIn', { delay: 250 });
    }

    public destroy(): void {
        if (!this.destroyed) {
            PlaySound('swoosh-2');
            StartAnimation(this.overlay, 'OverlayFadeOut')
            StartAnimation(this, 'MenuHide', { delay: 350 }).once(AnimationClipEvent.COMPLETE, () => {
                this.cleanupManager.cleanUpAll();
                super.destroy();
            });
        }
    }

    public showPurchaseError(error: string) {
        this.errorPopup = GameObject.Instantiate(SeasonPassPurchaseErrorPopup, { errorMessage: error });
        this.layout.root.addChild(this.errorPopup);
    }

    public hidePurchaseError() {
        if (this.errorPopup) {
            this.errorPopup.destroy();
            this.errorPopup = null;
        }
    }
}
